import type { IDocumentReaderUploadReference } from '@commenda/api/commenda-ai/document-reader/types';

import { CHAT_DOCUMENT_MENTIONS_PART_TYPE } from './markdown/chat-document-mentions';

/** How the transcript treats a `data-*` part. */
export type TDataPartKind =
  | 'hidden'
  | 'research-process'
  | 'error'
  | 'agent-plan'
  | 'document-upload'
  | 'generic';

export interface IDataPartEntry {
  partType: string;
  data: unknown;
}

export interface IResearchProcessAngle {
  angle: string;
  stepCount: number;
  /** Null until the angle's finding block arrives, which is what "still researching" looks like. */
  findingCount: number | null;
  hitCap: boolean;
}

export interface IResearchProcessSummary {
  angles: IResearchProcessAngle[];
  requirementCount: number | null;
  verdict: {
    checkedCount: number;
    verifiedCount: number;
    gaps: string[];
  } | null;
}

export interface IAgentPlanView {
  title: string;
  description: string;
  steps: { id: string; description: string }[];
}

const RESEARCH_STEP_PART_TYPE = 'data-research-researcher-step';
const RESEARCH_FINDING_PART_TYPE = 'data-research-finding';
const RESEARCH_NOTE_PART_TYPE = 'data-research-note';
const RESEARCH_VERDICT_PART_TYPE = 'data-research-verdict';
const VERIFIED_FIELD_STATUS = 'VERIFIED';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const asCount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Reads the `data-*` parts the shared read produces for research and the builder. The payloads are
 * untyped json written by other runtimes, so every field is read defensively. */
export class DataPartHelper {
  /** Already shown elsewhere: the clarify and plan questions by their cards and recaps, and a card
   * answer by the card it answered. Drawing them again only repeats what is on screen. */
  static readonly HIDDEN_PART_TYPES: readonly string[] = [
    'data-research-clarify',
    'data-research-plan',
    'data-question-answer',
    CHAT_DOCUMENT_MENTIONS_PART_TYPE,
  ];

  /** Research's working trace, which collapses into one section rather than a block per step. */
  static readonly RESEARCH_PROCESS_PART_TYPES: readonly string[] = [
    RESEARCH_STEP_PART_TYPE,
    RESEARCH_FINDING_PART_TYPE,
    RESEARCH_NOTE_PART_TYPE,
    RESEARCH_VERDICT_PART_TYPE,
  ];

  static readonly ERROR_PART_TYPES: readonly string[] = [
    'data-research-error',
    'data-agent-error',
  ];

  static readonly AGENT_PLAN_PART_TYPE = 'data-agent-plan';

  /** The data room a chat message refers to (stored by the backend, never an attachment). */
  static readonly DOCUMENT_UPLOAD_PART_TYPE = 'data-document-upload';

  static getKind(partType: string): TDataPartKind {
    if (DataPartHelper.HIDDEN_PART_TYPES.includes(partType)) return 'hidden';
    if (DataPartHelper.RESEARCH_PROCESS_PART_TYPES.includes(partType))
      return 'research-process';
    if (DataPartHelper.ERROR_PART_TYPES.includes(partType)) return 'error';
    if (partType === DataPartHelper.AGENT_PLAN_PART_TYPE) return 'agent-plan';
    if (partType === DataPartHelper.DOCUMENT_UPLOAD_PART_TYPE)
      return 'document-upload';
    return 'generic';
  }

  /** One line per research angle, in the order the angles first appear, plus the synthesis and
   * verification outcomes when they have arrived. */
  static summarizeResearchProcess(
    entries: readonly IDataPartEntry[]
  ): IResearchProcessSummary {
    const angles = new Map<string, IResearchProcessAngle>();
    let requirementCount: number | null = null;
    let verdict: IResearchProcessSummary['verdict'] = null;

    const angleFor = (angle: string): IResearchProcessAngle => {
      const existing = angles.get(angle);
      if (existing) return existing;
      const created = {
        angle,
        stepCount: 0,
        findingCount: null,
        hitCap: false,
      };
      angles.set(angle, created);
      return created;
    };

    entries.forEach(({ partType, data }) => {
      const record = asRecord(data);
      const payload = asRecord(record?.payload);
      const angle = asText(record?.source);

      if (partType === RESEARCH_STEP_PART_TYPE && angle) {
        angleFor(angle).stepCount += 1;
      } else if (partType === RESEARCH_FINDING_PART_TYPE && angle) {
        const entry = angleFor(angle);
        entry.findingCount = asCount(payload?.findingCount) ?? 0;
        entry.hitCap = payload?.hitCap === true;
      } else if (partType === RESEARCH_NOTE_PART_TYPE) {
        requirementCount = asCount(payload?.requirementCount);
      } else if (partType === RESEARCH_VERDICT_PART_TYPE) {
        const fields = Array.isArray(payload?.fields) ? payload.fields : [];
        const coverage = asRecord(payload?.coverage);
        const gaps = Array.isArray(coverage?.gaps)
          ? coverage.gaps.map(asText).filter(Boolean)
          : [];
        verdict = {
          checkedCount: fields.length,
          verifiedCount: fields.filter(
            (field) => asRecord(field)?.status === VERIFIED_FIELD_STATUS
          ).length,
          gaps,
        };
      }
    });

    return { angles: Array.from(angles.values()), requirementCount, verdict };
  }

  /** The builder's error carries its message at the top; research nests it in the payload and names
   * the angle that failed as the source. */
  static getErrorMessage(data: unknown): string {
    const record = asRecord(data);
    const message =
      asText(record?.message) || asText(asRecord(record?.payload)?.message);
    const angle = asText(record?.source);
    const text = message || 'Something went wrong.';

    return angle ? `${angle}: ${text}` : text;
  }

  static getAgentPlan(data: unknown): IAgentPlanView | null {
    const record = asRecord(data);
    if (!record) return null;
    const rawSteps = Array.isArray(record.steps) ? record.steps : [];
    const steps = rawSteps.flatMap((step, position) => {
      const stepRecord = asRecord(step);
      const description = asText(stepRecord?.description);
      if (!description) return [];
      // The builder gives every step an id; one without is still keyed stably by its place in the plan.
      return [
        { id: asText(stepRecord?.id) || `step-${position}`, description },
      ];
    });

    return {
      title: asText(record.title) || 'Plan',
      description: asText(record.description),
      steps,
    };
  }

  static getDocumentUpload(
    data: unknown
  ): IDocumentReaderUploadReference | null {
    const record = asRecord(data);
    const uploadId = asText(record?.uploadId);
    const fileCount = asCount(record?.fileCount);
    const totalSizeBytes = asCount(record?.totalSizeBytes);
    if (!uploadId || fileCount === null || totalSizeBytes === null) return null;

    return { uploadId, fileCount, totalSizeBytes };
  }
}
